import type { PillColor } from '@/ui'
import { BankingApplicationStatus } from './banking.types'

/**
 * Unit's API returns `applicationStatus` in PascalCase (e.g. `Approved`,
 * `PendingReview`); our enum (and the DB) is snake_case lowercase. Map at the
 * FE boundary so consumers can use the typed enum.
 */
const UNIT_TO_HEARD_STATUS: Record<string, BankingApplicationStatus> = {
  Approved: BankingApplicationStatus.Approved,
  Denied: BankingApplicationStatus.Denied,
  PendingReview: BankingApplicationStatus.PendingReview,
  AwaitingDocuments: BankingApplicationStatus.AwaitingDocuments,
  Canceled: BankingApplicationStatus.Canceled,
  Pending: BankingApplicationStatus.Pending,
}

type UnitApplicationFormCompletedDetail = {
  data?: {
    attributes?: {
      applicationStatus?: string
    }
  }
}

/**
 * Read the applicationStatus from a Unit `unitApplicationFormCompleted` event
 * and return it as our typed enum. Unit ships `detail` as a Promise that
 * resolves to the payload, so we await it before reading. Returns null when
 * the payload is missing the field or carries a status we don't know about.
 */
export const parseUnitApplicationFormCompletedStatus = async (
  event: Event
): Promise<BankingApplicationStatus | null> => {
  if (!(event instanceof CustomEvent)) return null
  const detail: UnitApplicationFormCompletedDetail | undefined =
    await event.detail
  const raw = detail?.data?.attributes?.applicationStatus
  if (!raw) return null
  return UNIT_TO_HEARD_STATUS[raw] ?? null
}

const STATUS_LABELS: Record<BankingApplicationStatus, string> = {
  [BankingApplicationStatus.Pending]: 'Pending',
  [BankingApplicationStatus.PendingReview]: 'Pending review',
  [BankingApplicationStatus.Approved]: 'Approved',
  [BankingApplicationStatus.AwaitingDocuments]: 'Awaiting documents',
  [BankingApplicationStatus.Denied]: 'Denied',
  [BankingApplicationStatus.Canceled]: 'Canceled',
}

const STATUS_COLORS: Record<BankingApplicationStatus, PillColor> = {
  [BankingApplicationStatus.Pending]: 'yellow',
  [BankingApplicationStatus.PendingReview]: 'yellow',
  [BankingApplicationStatus.AwaitingDocuments]: 'yellow',
  [BankingApplicationStatus.Approved]: 'green',
  [BankingApplicationStatus.Denied]: 'red',
  [BankingApplicationStatus.Canceled]: 'gray',
}

export const humanizeStatus = (status: BankingApplicationStatus): string =>
  STATUS_LABELS[status]

export const statusColor = (status: BankingApplicationStatus): PillColor =>
  STATUS_COLORS[status]
